import React, { useState, useEffect } from 'react';
import { Button, Typography, Row, Col, Card, Input, message, Space, Modal, Divider, Select } from 'antd';
import { CheckCircleOutlined, MailOutlined, ArrowRightOutlined, SwapOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './SubscribePage.css';
import api from '../api';

const { Title, Text, Paragraph } = Typography;

interface PricingTier {
  seats: number;
  name: string;
  focusPrice: number;
  inSightPrice: number;
  panoramaPrice: number;
  focusMonthly: number;
  inSightMonthly: number;
  panoramaMonthly: number;
}

const pricingTiers: PricingTier[] = [
  {
    seats: 1,
    name: 'Single User',
    focusPrice: 1800,
    inSightPrice: 3600,
    panoramaPrice: 6000,
    focusMonthly: 150,
    inSightMonthly: 300,
    panoramaMonthly: 500
  },
  {
    seats: 5,
    name: 'Company (Single Site)',
    focusPrice: 5400,
    inSightPrice: 10800,
    panoramaPrice: 18000,
    focusMonthly: 450,
    inSightMonthly: 900,
    panoramaMonthly: 1500
  },
  {
    seats: 20,
    name: 'Company (Global)',
    focusPrice: 12000,
    inSightPrice: 24000,
    panoramaPrice: 42000,
    focusMonthly: 1000,
    inSightMonthly: 2000,
    panoramaMonthly: 3500
  }
];

const focusTopics = [
  // Dermatology domain
  'Psoriasis', 
  'Skin Cancer',
  // Ophthalmology domain
  'Retinal',
  'Glaucoma'
];
const inSightDomains = [
  { value: 'Dermatology', label: 'Dermatology + Psoriasis + Skin Cancer' },
  { value: 'Ophthalmology', label: 'Ophthalmology + Retinal + Glaucoma' },
  { value: 'Cell, Gene & RNA', label: 'Cell, Gene & RNA Therapies' }
];

const planDescriptions = {
  focus: {
    title: 'Focus',
    description: 'Single therapeutic area coverage with comprehensive market intelligence',
    features: ['1 therapeutic area', 'Email support']
  },
  'in-sight': {
    title: 'In Sight',
    description: 'Multiple therapeutic areas with cross-domain analysis capabilities',
    features: ['Multiple Focus areas', 'Cross-area benchmarking', 'Priority support']
  },
  panorama: {
    title: 'Panorama',
    description: 'Complete platform access with all therapeutic areas and custom features',
    features: ['All therapeutic areas', 'Cross-area benchmarking', 'Custom analytics', 'Dedicated support']
  }
};

// Personal email domains to block
const personalEmailDomains = [
  'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'aol.com',
  'icloud.com', 'mail.com', 'protonmail.com', 'yandex.com', 'zoho.com',
  'gmx.com', 'live.com', 'msn.com', 'ymail.com', 'me.com'
];

const SubscribePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPlan = searchParams.get('plan') || 'focus';
  
  const [selectedPlan, setSelectedPlan] = useState<string>(initialPlan);
  const [selectedTier, setSelectedTier] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [focusTopic, setFocusTopic] = useState<string>(focusTopics[0]);
  const [inSightDomain, setInSightDomain] = useState<string>(inSightDomains[0].value);

  useEffect(() => {
    // Scroll to top when component mounts or route changes
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Update selected plan if URL parameter changes
    const plan = searchParams.get('plan');
    if (plan && ['focus', 'in-sight', 'panorama'].includes(plan)) {
      setSelectedPlan(plan);
    }
    // Scroll to top when plan changes
    window.scrollTo(0, 0);
  }, [searchParams]);

  useEffect(() => {
    if (selectedPlan !== 'focus') {
      setFocusTopic(focusTopics[0]);
    }
    if (selectedPlan !== 'in-sight') {
      setInSightDomain(inSightDomains[0].value);
    }
  }, [selectedPlan]);

  const validateCorporateEmail = (email: string): boolean => {
    if (!email || !email.includes('@')) return false;
    
    const domain = email.split('@')[1].toLowerCase();
    return !personalEmailDomains.includes(domain);
  };

  const getPrice = (tier: PricingTier): number => {
    switch (selectedPlan) {
      case 'focus':
        return tier.focusPrice;
      case 'in-sight':
        return tier.inSightPrice;
      case 'panorama':
        return tier.panoramaPrice;
      default:
        return tier.focusPrice;
    }
  };

  const getMonthlyPrice = (tier: PricingTier): number => {
    switch (selectedPlan) {
      case 'focus':
        return tier.focusMonthly;
      case 'in-sight':
        return tier.inSightMonthly;
      case 'panorama':
        return tier.panoramaMonthly;
      default:
        return tier.focusMonthly;
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Clear error when user starts typing
    if (emailError) {
      setEmailError('');
    }
  };

  const handleContinueToPayment = async () => {
    // Clear previous email error
    setEmailError('');
    
    // Validate selections
    if (selectedTier === null) {
      message.error('Please select a license type');
      return;
    }

    if (!email) {
      setEmailError('Please enter your email address');
      return;
    }

    if (!validateCorporateEmail(email)) {
      setEmailError('Please use your official company email address. Personal email addresses (Gmail, Yahoo, etc.) are not accepted.');
      return;
    }

    setIsSubmitting(true);

    try {
      const tier = pricingTiers[selectedTier];
      const amount = getPrice(tier);
      
      const requestData = {
        plan: selectedPlan,
        tier: tier.name,
        seats: tier.seats,
        amount: amount,
        email: email,
        topic: selectedPlan === 'focus' ? focusTopic : undefined,
        domain: selectedPlan === 'in-sight' ? inSightDomain : undefined
      };
      
      console.log('DEBUG: Sending checkout request:', requestData);
      console.log('DEBUG: API base URL:', api.defaults.baseURL);
      
      // Call backend to create Stripe Checkout session
      const response = await api.post('/api/create-checkout-session', requestData);

      console.log('DEBUG: Checkout response:', response.data);

      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      console.error('Error response:', error.response?.data);
      message.error(error.response?.data?.detail || 'Failed to initiate payment. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleChangePlan = (newPlan: string) => {
    setSelectedPlan(newPlan);
    setShowPlanModal(false);
    navigate(`/subscribe?plan=${newPlan}`);
  };

  const currentPlanInfo = planDescriptions[selectedPlan as keyof typeof planDescriptions];

  return (
    <div className="subscribe-page">
      {/* Navigation */}
      <nav className="subscribe-navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <img src="/sns-focus-logo.png" alt="SNS Intelligence" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
          </div>
        </div>
      </nav>

      {/* Back to Home Button */}
      <div className="back-to-home-section">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          className="back-to-home-button"
        >
          Back to Home
        </Button>
      </div>

      {/* Main Content */}
      <section className="subscribe-section">
        <div className="subscribe-container">
          <div className="subscribe-header">
            {/* Plan Info - Above "Choose Your License" */}
            <Title level={2} className="plan-name-large">
              {currentPlanInfo.title}
            </Title>
            <Paragraph className="plan-description">
              {currentPlanInfo.description}
            </Paragraph>
            
            {/* Plan Features - Show once under plan name */}
            <div className="plan-features-section">
              {currentPlanInfo.features.map((feature, idx) => (
                <div key={idx} className="plan-feature-item">
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                  <Text>{feature}</Text>
                </div>
              ))}
            </div>
          
          {selectedPlan === 'focus' && (
            <div className="focus-topic-selection">
              <Title level={4}>Select Your Focus Area</Title>
              <Select
                value={focusTopic}
                onChange={(value) => setFocusTopic(value)}
                options={focusTopics.map(topic => ({ value: topic, label: topic }))}
                size="large"
                style={{ width: '100%', maxWidth: '360px' }}
              />
            </div>
          )}

          {selectedPlan === 'in-sight' && (
            <div className="focus-topic-selection">
              <Title level={4}>Select your In Sight Domain</Title>
              <Select
                value={inSightDomain}
                onChange={(value) => setInSightDomain(value)}
                options={inSightDomains}
                size="large"
                style={{ width: '100%', maxWidth: '360px' }}
              />
            </div>
          )}

            {/* Choose Your License Title - Below Plan Info */}
            <Title level={1} className="subscribe-title" style={{ marginTop: '32px' }}>
              Choose Your License
            </Title>
          </div>

          {/* Pricing Cards */}
          <Row gutter={[24, 24]} justify="center" style={{ marginTop: '48px' }}>
            {pricingTiers.map((tier, index) => (
              <Col xs={24} md={8} key={index}>
                <Card
                  className={`pricing-card ${selectedTier === index ? 'selected' : ''}`}
                  onClick={() => setSelectedTier(index)}
                  hoverable
                >
                  <div className="pricing-card-header">
                    <Title level={3}>{tier.name}</Title>
                    <Text className="seats-info-large">{tier.seats} seat{tier.seats > 1 ? 's' : ''}</Text>
                  </div>
                  <div className="pricing-card-price">
                    <Title level={1} className="monthly-price-large">${getMonthlyPrice(tier)}</Title>
                    <Text className="price-period-small">per month</Text>
                    <Text className="price-yearly-small">${getPrice(tier).toLocaleString()} per year</Text>
                  </div>
                  {selectedTier === index && (
                    <div className="selected-badge">
                      <CheckCircleOutlined /> Selected
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>

          {/* View Other Plans Button - Below Pricing Cards */}
          <div className="view-other-plans-section" style={{ marginTop: '32px' }}>
            <Button
              type="default"
              size="large"
              icon={<SwapOutlined />}
              onClick={() => setShowPlanModal(true)}
              className="view-other-plans-button"
            >
              View Other Plans
            </Button>
          </div>

          {/* Email Input */}
          <div className="email-input-section">
            <Title level={4}>Company Email Address</Title>
            <Input
              size="large"
              placeholder="your.name@company.com"
              prefix={<MailOutlined />}
              value={email}
              onChange={handleEmailChange}
              className={`email-input ${emailError ? 'error' : ''}`}
              type="email"
            />
            {emailError ? (
              <Text className="email-error">
                {emailError}
              </Text>
            ) : (
              <Text className="email-help">
                Please use your official company email address. Personal email addresses (Gmail, Yahoo, etc.) are not accepted.
              </Text>
            )}
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <Button
              type="primary"
              size="large"
              icon={<ArrowRightOutlined />}
              onClick={handleContinueToPayment}
              loading={isSubmitting}
              disabled={selectedTier === null}
              className="continue-button"
            >
              Continue to Payment
            </Button>
          </div>

          {/* Billing Information */}
          <div className="billing-info">
            <Text className="info-text">
              All plans are billed annually (12-month access). Monthly prices shown for comparison.
            </Text>
            <Text className="info-text">
              Company licenses include individual logins for each user (no shared credentials).
            </Text>
          </div>
        </div>
      </section>

      <div className="footer-contact-line">
        <Text>
          Have questions? Contact us at{' '}
          <a
            href="mailto:info@snsintel.com"
            className="footer-contact-link"
          >
            info@snsintel.com
          </a>
        </Text>
      </div>
      {/* Footer */}
      <footer className="subscribe-footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-logo-section">
              <div className="footer-logo">
                <img src="/sns-focus-logo.png" alt="SNS Intelligence" />
              </div>
            </div>
            <div className="footer-links-section">
              <div className="footer-column">
                <Title level={5}>Product</Title>
                <div className="footer-links">
                  <a href="/platform">Platform</a>
                  <a href="/data-coverage">Data Coverage</a>
                  <a href="/markets">Markets</a>
                  <a href="/pulse">Pulse</a>
                </div>
              </div>
              <div className="footer-column">
                <Title level={5}>Company</Title>
                <div className="footer-links">
                  <a href="/about">About</a>
                  <a href="/contact">Contact</a>
                  <a href="/press">Press</a>
                </div>
              </div>
            </div>
          </div>
          <Divider />
          <div className="footer-bottom">
            <Text>© 2026 SNS Intelligence. All rights reserved.</Text>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Change Plan Modal */}
      <Modal
        title="Choose a Different Plan"
        open={showPlanModal}
        onCancel={() => setShowPlanModal(false)}
        footer={null}
        width={1000}
      >
        <div style={{ padding: '40px 0' }}>
          <Row gutter={[24, 24]} justify="center" style={{ display: 'flex', alignItems: 'stretch' }}>
            {Object.entries(planDescriptions).map(([key, plan]) => {
              const isPanorama = key === 'panorama';
              return (
                <Col xs={24} md={7} key={key} style={{ display: 'flex' }}>
                  <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {isPanorama && (
                      <div style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        color: '#8c8c8c',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid rgba(140, 140, 140, 0.3)',
                        fontSize: '12px',
                        fontWeight: 600,
                        zIndex: 10,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}>
                        Coming Soon
                      </div>
                    )}
                    <Card
                      hoverable={!isPanorama}
                      className={`plan-modal-card ${selectedPlan === key ? 'selected' : ''} ${isPanorama ? 'disabled' : ''}`}
                      onClick={isPanorama ? undefined : () => handleChangePlan(key)}
                      style={{ 
                        cursor: isPanorama ? 'not-allowed' : 'pointer',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Title level={4}>{plan.title}</Title>
                      <Paragraph>{plan.description}</Paragraph>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                        {plan.features.map((feature, idx) => (
                          <div key={idx} style={{ marginBottom: '8px' }}>
                            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                            <Text>{feature}</Text>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>
      </Modal>
    </div>
  );
};

export default SubscribePage;

